import React from 'react';
import { WorkingHour } from '../../types/api';
import { isRoundTheClock } from '../../utils/workingHours';

interface WorkingHoursPanelProps {
  hours: WorkingHour[];
  onEdit: () => void;
}

/** Пн…Вс: у бэка неделя начинается с воскресенья, у читателя — с понедельника. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];
const MINUTES_IN_DAY = 24 * 60;

function toMinutes(time: string | null): number | null {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  if (Number.isNaN(hours)) return null;
  return hours * 60 + (minutes || 0);
}

/** Отрезок дня на шкале в процентах; null — у выходного и у дня без времён. */
function spanOf(day: WorkingHour): { left: number; width: number } | null {
  if (isRoundTheClock(day)) return { left: 0, width: 100 };
  const open = toMinutes(day.open_time);
  const closeRaw = toMinutes(day.close_time);
  if (day.is_day_off || open == null || closeRaw == null) return null;
  const close = day.closes_next_day ? closeRaw + MINUTES_IN_DAY : closeRaw;
  if (close <= open) return null;
  const left = (open / MINUTES_IN_DAY) * 100;
  return { left, width: Math.min(100 - left, ((close - open) / MINUTES_IN_DAY) * 100) };
}

function labelOf(day: WorkingHour): string {
  if (day.is_day_off) return 'выходной';
  if (isRoundTheClock(day)) return 'круглосуточно';
  return `${day.open_time?.slice(0, 5) ?? '—'}–${day.close_time?.slice(0, 5) ?? '—'}${
    day.closes_next_day ? '⁺¹' : ''
  }`;
}

/**
 * Режим работы отрезком на суточной шкале, а не строкой «08:00 — 00:00».
 *
 * Смысл в том, чтобы разница между днями читалась одним взглядом: где
 * заведение открывается позже, где работает за полночь. Ночной хвост
 * (`closes_next_day`) красится янтарём. Круглосуточный день (ADR-027) —
 * вся шкала без янтаря: его 00:00 → 00:00⁺¹ формально ночная смена, но
 * за полночь он не уходит, и подпись у него словом, а не «00:00–00:00⁺¹».
 */
export const WorkingHoursPanel: React.FC<WorkingHoursPanelProps> = ({ hours, onEdit }) => {
  const ordered = [...hours].sort(
    (a, b) => WEEK_ORDER.indexOf(a.day_of_week) - WEEK_ORDER.indexOf(b.day_of_week),
  );

  return (
    <section className="qp-panel">
      <header className="qp-panel-head">
        <h2 className="qp-label">Режим работы</h2>
        <button type="button" className="qp-link" onClick={onEdit}>
          Изменить
        </button>
      </header>

      {ordered.length === 0 ? (
        <p className="qp-faint" style={{ margin: 0, fontSize: 13 }}>
          Расписание не задано — в приложении заведение будет показано без часов работы.
        </p>
      ) : (
        <div className="qp-hours">
          {ordered.map((day) => {
            const span = spanOf(day);
            const isNightTail = day.closes_next_day && !isRoundTheClock(day);

            return (
              <div key={day.id} className="qp-hours-row">
                <span className="qp-hours-day">{day.day_label}</span>
                <span className="qp-hours-track">
                  {span && (
                    <span
                      className={`qp-hours-span${isNightTail ? ' qp-hours-span--next' : ''}`}
                      style={{ left: `${span.left}%`, width: `${span.width}%` }}
                    />
                  )}
                </span>
                <span className="qp-hours-label">{labelOf(day)}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
